import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  RefreshCw,
  CheckCircle2,
  Circle,
  ArrowLeft,
  Lock,
  Inbox,
  Images,
  UploadCloud,
  Trash2,
  Play,
  MessageSquareQuote,
} from "lucide-react";
import { NavyogaLogo } from "@/components/NavyogaLogo";
import { AdminReviews } from "@/components/AdminReviews";
import { API } from "@/lib/site";

const TOKEN_KEY = "navyoga_admin_token";

const KIND_OPTIONS = [
  { value: "gallery-photo", label: "Gallery — Photo" },
  { value: "gallery-video", label: "Gallery — Video" },
  { value: "review-photo", label: "Review — Screenshot / Photo" },
  { value: "review-video", label: "Review — Video" },
];

const CATEGORY_OPTIONS = ["Morning Batches", "Wellness Camps", "Community Sessions"];

export const mediaUrl = (id) => `${API}/media/file/${id}`;

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(null);
  const [tab, setTab] = useState("enquiries");

  const [media, setMedia] = useState([]);
  const [kind, setKind] = useState("gallery-photo");
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState(CATEGORY_OPTIONS[0]);
  const [reviewer, setReviewer] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/messages`, authHeaders());
      setMessages(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load messages.");
      }
    }
  }, [authHeaders]);

  const loadMedia = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/media`);
      setMedia(data);
    } catch {
      toast.error("Could not load media library.");
    }
  }, []);

  useEffect(() => {
    if (token) {
      loadMessages();
      loadMedia();
    }
  }, [token, loadMessages, loadMedia]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMessages(null);
  };

  const toggleContacted = async (msg) => {
    try {
      const { data } = await axios.patch(`${API}/admin/messages/${msg.id}`, { contacted: !msg.contacted }, authHeaders());
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? data : m)));
    } catch {
      toast.error("Could not update the message.");
    }
  };

  const uploadMedia = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a file first.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", kind);
      formData.append("label", label);
      if (kind.startsWith("gallery")) formData.append("category", category);
      if (kind.startsWith("review")) formData.append("reviewer", reviewer);
      const { data } = await axios.post(`${API}/admin/media`, formData, authHeaders());
      setMedia((prev) => [data, ...prev]);
      setLabel("");
      setReviewer("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Uploaded! It is now live on the website.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = async (item) => {
    try {
      await axios.delete(`${API}/admin/media/${item.id}`, authHeaders());
      setMedia((prev) => prev.filter((m) => m.id !== item.id));
      toast.success("Removed from the website.");
    } catch {
      toast.error("Could not delete. Please try again.");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-slate-500 focus:border-[#2E8B57] focus:ring-4 focus:ring-[#2E8B57]/20";

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#0B1912] text-slate-200">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-8">
        <div className="flex items-center justify-between">
          <NavyogaLogo className="h-12" />
          <Link
            to="/"
            data-testid="admin-back-to-site-link"
            className="flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-emerald-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>

        {!token ? (
          <div className="mx-auto mt-16 max-w-md">
            <form
              data-testid="admin-login-form"
              onSubmit={login}
              className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur sm:p-10"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2E8B57] text-white">
                <Lock className="h-5 w-5" />
              </div>
              <h1 className="font-heading mt-5 text-3xl font-semibold text-[#F8FFF8]">Studio Login</h1>
              <p className="mt-2 text-sm text-slate-400">View enquiries and upload photos & videos to your website.</p>

              <label htmlFor="admin-email" className="mb-2 mt-7 block text-xs font-bold uppercase tracking-wider text-slate-400">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@example.com"
              />
              <label htmlFor="admin-password" className="mb-2 mt-5 block text-xs font-bold uppercase tracking-wider text-slate-400">
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />

              {error && (
                <p data-testid="admin-login-error" className="mt-4 rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {error}
                </p>
              )}

              <button
                data-testid="admin-login-submit-button"
                type="submit"
                disabled={loading}
                className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#2E8B57] px-6 py-3.5 text-sm font-semibold text-white transition-[transform,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#267a4b] disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
        ) : (
          <div className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex gap-2">
                <button
                  data-testid="admin-tab-enquiries"
                  onClick={() => setTab("enquiries")}
                  className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                    tab === "enquiries" ? "bg-[#2E8B57] text-white" : "border border-white/15 text-slate-300 hover:text-emerald-300"
                  }`}
                >
                  <Inbox className="h-4 w-4" />
                  Enquiries
                </button>
                <button
                  data-testid="admin-tab-media"
                  onClick={() => setTab("media")}
                  className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                    tab === "media" ? "bg-[#2E8B57] text-white" : "border border-white/15 text-slate-300 hover:text-emerald-300"
                  }`}
                >
                  <Images className="h-4 w-4" />
                  Photos & Videos
                </button>
                <button
                  data-testid="admin-tab-reviews"
                  onClick={() => setTab("reviews")}
                  className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                    tab === "reviews" ? "bg-[#2E8B57] text-white" : "border border-white/15 text-slate-300 hover:text-emerald-300"
                  }`}
                >
                  <MessageSquareQuote className="h-4 w-4" />
                  Reviews
                </button>
              </div>
              <div className="flex gap-3">
                {tab === "enquiries" && (
                  <button
                    data-testid="admin-refresh-button"
                    onClick={loadMessages}
                    className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:border-[#2E8B57] hover:text-emerald-300"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </button>
                )}
                <button
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:border-red-400 hover:text-red-300"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>

            {tab === "reviews" ? (
              <AdminReviews authHeaders={authHeaders} />
            ) : tab === "enquiries" ? (
              <>
                <div className="mt-8">
                  <h1 className="font-heading text-3xl font-semibold text-[#F8FFF8] sm:text-4xl">Enquiry Inbox</h1>
                  <p data-testid="admin-message-count" className="mt-1 text-sm text-slate-400">
                    {messages ? `${messages.length} message${messages.length === 1 ? "" : "s"}` : "Loading…"}
                  </p>
                </div>
                <div data-testid="admin-messages-list-table" className="mt-8 space-y-4">
                  {messages === null ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="h-8 w-8 animate-spin text-[#2E8B57]" />
                    </div>
                  ) : messages.length === 0 ? (
                    <p data-testid="admin-empty-state" className="rounded-3xl border border-dashed border-white/15 py-20 text-center text-sm text-slate-400">
                      No enquiries yet. New form submissions will appear here.
                    </p>
                  ) : (
                    messages.map((m) => (
                      <div
                        key={m.id}
                        data-testid={`admin-message-${m.id}`}
                        className={`rounded-2xl border p-6 transition-colors duration-300 ${
                          m.contacted ? "border-[#2E8B57]/30 bg-[#2E8B57]/5" : "border-white/10 bg-white/5"
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-base font-semibold text-[#F8FFF8]">{m.name}</p>
                            <p className="mt-0.5 text-sm text-slate-400">
                              {m.phone}
                              {m.email ? ` · ${m.email}` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            {m.therapy && (
                              <span className="rounded-full bg-[#1E3A8A]/40 px-3.5 py-1.5 text-xs font-semibold text-blue-200">
                                {m.therapy}
                              </span>
                            )}
                            <button
                              data-testid={`admin-mark-contacted-${m.id}`}
                              onClick={() => toggleContacted(m)}
                              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                                m.contacted
                                  ? "bg-[#2E8B57]/20 text-emerald-300"
                                  : "bg-white/10 text-slate-400 hover:text-emerald-300"
                              }`}
                            >
                              {m.contacted ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                              {m.contacted ? "Contacted" : "Mark contacted"}
                            </button>
                          </div>
                        </div>
                        <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{m.message}</p>
                        <p className="mt-3 text-xs text-slate-500">
                          {new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="mt-8">
                  <h1 className="font-heading text-3xl font-semibold text-[#F8FFF8] sm:text-4xl">Photos & Videos</h1>
                  <p className="mt-1 text-sm text-slate-400">
                    Upload class photos, session videos and student reviews — they go live on the website instantly.
                  </p>
                </div>

                <form
                  data-testid="admin-upload-form"
                  onSubmit={uploadMedia}
                  className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-6 sm:p-8"
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="upload-kind" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                        Upload Type
                      </label>
                      <select
                        id="upload-kind"
                        data-testid="admin-upload-kind-select"
                        value={kind}
                        onChange={(e) => {
                          setKind(e.target.value);
                          setFile(null);
                          if (fileRef.current) fileRef.current.value = "";
                        }}
                        className={`${inputCls} [&>option]:bg-[#0B1912]`}
                      >
                        {KIND_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="upload-label" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                        Title (shown on site)
                      </label>
                      <input
                        id="upload-label"
                        data-testid="admin-upload-label-input"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        className={inputCls}
                        placeholder={kind.startsWith("review") ? "Short highlight from the review" : "e.g. Sunday Wellness Camp"}
                      />
                    </div>
                    {kind.startsWith("gallery") && (
                      <div>
                        <label htmlFor="upload-category" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                          Gallery Category
                        </label>
                        <select
                          id="upload-category"
                          data-testid="admin-upload-category-select"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className={`${inputCls} [&>option]:bg-[#0B1912]`}
                        >
                          {CATEGORY_OPTIONS.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {kind.startsWith("review") && (
                      <div>
                        <label htmlFor="upload-reviewer" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                          Student Name
                        </label>
                        <input
                          id="upload-reviewer"
                          data-testid="admin-upload-reviewer-input"
                          value={reviewer}
                          onChange={(e) => setReviewer(e.target.value)}
                          className={inputCls}
                          placeholder="e.g. Geeta"
                        />
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <label htmlFor="upload-file" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                        {kind.endsWith("video") ? "Video File" : "Image File"}
                      </label>
                      <input
                        id="upload-file"
                        ref={fileRef}
                        data-testid="admin-upload-file-input"
                        type="file"
                        required
                        accept={kind.endsWith("video") ? "video/*" : "image/*"}
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="w-full rounded-xl border border-dashed border-white/20 bg-white/5 px-4 py-6 text-sm text-slate-300 file:mr-4 file:rounded-full file:border-0 file:bg-[#2E8B57] file:px-5 file:py-2.5 file:text-sm file:font-semibold file:text-white"
                      />
                    </div>
                  </div>
                  <button
                    data-testid="admin-upload-submit-button"
                    type="submit"
                    disabled={uploading}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2E8B57] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#267a4b] disabled:opacity-60"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                    {uploading ? "Uploading… (videos may take a minute)" : "Upload to Website"}
                  </button>
                </form>

                <div data-testid="admin-media-library" className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {media.length === 0 ? (
                    <p
                      data-testid="admin-media-empty-state"
                      className="col-span-full rounded-3xl border border-dashed border-white/15 py-16 text-center text-sm text-slate-400"
                    >
                      Nothing uploaded yet. Your uploads will appear here and on the live sections of the site.
                    </p>
                  ) : (
                    media.map((item) => (
                      <div
                        key={item.id}
                        data-testid={`admin-media-item-${item.id}`}
                        className="group overflow-hidden rounded-2xl border border-white/10 bg-white/5"
                      >
                        <div className="relative aspect-[4/3] bg-black/40">
                          {item.kind.endsWith("video") ? (
                            <>
                              <video src={mediaUrl(item.id)} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                              <span className="absolute inset-0 flex items-center justify-center">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white">
                                  <Play className="h-4 w-4" />
                                </span>
                              </span>
                            </>
                          ) : (
                            <img src={mediaUrl(item.id)} alt={item.label || "Uploaded media"} loading="lazy" className="h-full w-full object-cover" />
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 p-3">
                          <div className="min-w-0">
                            <p className="truncate text-xs font-semibold text-slate-200">{item.label || item.reviewer || "Untitled"}</p>
                            <p className="truncate text-[10px] uppercase tracking-wider text-slate-500">
                              {KIND_OPTIONS.find((o) => o.value === item.kind)?.label || item.kind}
                              {item.category ? ` · ${item.category}` : ""}
                            </p>
                          </div>
                          <button
                            data-testid={`admin-media-delete-${item.id}`}
                            onClick={() => deleteMedia(item)}
                            className="rounded-full p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-300"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
